<template>
  <div class="subscription-page">
    <div class="page-container">
      <!-- Page Header -->
      <div class="page-header">
        <h1 class="page-title">{{ $t('subscription.title') }}</h1>
        <p class="page-subtitle">{{ $t('subscription.subtitle') }}</p>
      </div>

      <!-- Current Plan Card -->
      <div class="current-plan-card">
        <div class="plan-badge">{{ $t('subscription.currentPlan') }}</div>
        <h3 class="current-plan-title">{{ planName }}</h3>
        <p class="current-plan-subtitle">{{ planSubtitle }}</p>

        <div class="usage-section">
          <div class="usage-header">
            <span class="usage-label">{{ $t('subscription.invoiceScans') }}</span>
            <span class="usage-count">{{ scansUsed }} / {{ scanLimit }} {{ $t('subscription.thisMonth') }}</span>
          </div>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: `${usagePercentage}%` }"></div>
          </div>

          <div v-if="hardScanLimit > 0" class="usage-header" style="margin-top: 16px;">
            <span class="usage-label">{{ $t('subscription.hardScans') }}</span>
            <span class="usage-count">{{ hardScansUsed }} / {{ hardScanLimit }}</span>
          </div>
          <div v-if="hardScanLimit > 0" class="progress-bar">
            <div class="progress-fill progress-fill-premium" :style="{ width: `${hardScanUsagePercentage}%` }"></div>
          </div>

          <p class="usage-note">{{ $t('subscription.periodEnds') }} {{ formatPeriodEnd }}</p>

          <button
            v-if="subscription?.stripe_subscription_id"
            @click="openBillingPortal"
            class="btn-manage-subscription"
          >
            <font-awesome-icon icon="fa-solid fa-gear" />
            {{ $t('subscription.manageSubscription') }}
          </button>
        </div>
      </div>

      <!-- Bank-transfer customers ask us for an invoice here instead of calling. -->
      <InvoiceRequestCard />

      <!-- Card customers get at the invoices Stripe already issued them. -->
      <BillingInvoicesCard />

      <!-- Pricing Plans -->
      <div v-if="loading" class="loading-plans">
        <div class="spinner"></div>
        <p>{{ $t('subscription.loadingPlans') }}</p>
      </div>

      <div v-else class="plans-grid">
        <!-- Dynamic Plans from Config -->
        <div
          v-for="plan in plans"
          :key="plan.id"
          :class="['plan-card', {
            'featured': plan.is_featured && currentPlanKey !== plan.id,
            'current': currentPlanKey === plan.id
          }]"
        >
          <div v-if="currentPlanKey === plan.id" class="current-plan-badge">{{ $t('subscription.currentPlanBadge') }}</div>
          <div v-else-if="plan.is_featured" class="featured-badge">{{ $t('subscription.mostPopular') }}</div>

          <div class="plan-header">
            <h3 class="plan-name">{{ plan.name.replace(' Plan', '') }}</h3>
            <div class="plan-price">
              <span class="price-amount">{{ plan.price === 0 ? '0' : plan.price }} EUR</span>
              <span v-if="plan.price > 0" class="price-period">{{ $t('subscription.perMonth') }}</span>
            </div>
          </div>

          <ul class="plan-features">
            <li v-for="(feature, key) in plan.features" :key="key" class="feature-item">
              <font-awesome-icon icon="fa-solid fa-circle-check" class="feature-icon" />
              <span>{{ translateFeature(feature) }}</span>
            </li>
            <!-- Read from the plan rather than written into its feature list, so
                 the number can never drift from the one the limit is enforced on. -->
            <li v-if="plan.hard_scan_limit > 0" class="feature-item">
              <font-awesome-icon icon="fa-solid fa-circle-check" class="feature-icon" />
              <span>{{ $t('pricing.hardScanLimit', { limit: plan.hard_scan_limit }) }}</span>
            </li>
          </ul>

          <button
            v-if="currentPlanKey === plan.id"
            class="plan-btn current"
            disabled
          >
            {{ $t('subscription.currentPlanBadge') }}
          </button>
          <button
            v-else-if="!plan.is_free"
            @click="upgradeToPlan(plan)"
            :class="['plan-btn', { 'featured': plan.is_featured }]"
          >
            {{ getPlanButtonText(plan) }}
          </button>
        </div>


      </div>

      <!-- FAQ Section -->
      <div class="faq-section">
        <h2 class="faq-title">{{ $t('subscription.faqTitle') }}</h2>
        <div class="faq-grid">
          <div class="faq-item">
            <h3 class="faq-question">{{ $t('subscription.faqQuestion1') }}</h3>
            <p class="faq-answer">{{ $t('subscription.faqAnswer1') }}</p>
          </div>
          <div class="faq-item">
            <h3 class="faq-question">{{ $t('subscription.faqQuestion2') }}</h3>
            <p class="faq-answer">{{ $t('subscription.faqAnswer2') }}</p>
          </div>
          <div class="faq-item">
            <h3 class="faq-question">{{ $t('subscription.faqQuestion3') }}</h3>
            <p class="faq-answer">{{ $t('subscription.faqAnswer3') }}</p>
          </div>
          <div class="faq-item">
            <h3 class="faq-question">{{ $t('subscription.faqQuestion4') }}</h3>
            <p class="faq-answer">{{ $t('subscription.faqAnswer4') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import api from '@/utils/api';
import { useI18n } from 'vue-i18n';
import InvoiceRequestCard from '@/components/subscription/InvoiceRequestCard.vue';
import BillingInvoicesCard from '@/components/subscription/BillingInvoicesCard.vue';

const { t } = useI18n();

const authStore = useAuthStore();
const uiStore = useUiStore();

const plans = ref([]);
const loading = ref(true);

const subscription = computed(() => authStore.subscription);

const scansUsed = computed(() => subscription.value?.scans_used || 0);
const scanLimit = computed(() => subscription.value?.scan_limit || 10);
const hardScansUsed = computed(() => subscription.value?.hard_scans_used || 0);
const hardScanLimit = computed(() => subscription.value?.hard_scan_limit || 0);

const usagePercentage = computed(() => {
  if (!scanLimit.value) return 0;
  return Math.min((scansUsed.value / scanLimit.value) * 100, 100);
});

const hardScanUsagePercentage = computed(() => {
  if (!hardScanLimit.value) return 0;
  return Math.min((hardScansUsed.value / hardScanLimit.value) * 100, 100);
});

const currentPlanKey = computed(() => {
  // Check if user has a valid paid subscription (with Stripe IDs)
  const hasStripeSubscription = subscription.value?.stripe_subscription_id || subscription.value?.stripe_price_id;

  // If no Stripe subscription, always return 'free' regardless of scan_limit
  if (!hasStripeSubscription) {
    return 'free';
  }

  // Find plan by matching stripe_price_id
  const priceId = subscription.value?.stripe_price_id;
  if (priceId) {
    const plan = plans.value.find(p => p.price_id === priceId);
    if (plan) {
      return plan.id;
    }
  }

  // Fallback to scan_limit if price_id not found
  const limit = scanLimit.value;
  if (limit <= 10) return 'free';
  if (limit <= 1000) return 'pro';
  if (limit <= 10000) return 'ultimate';
  return 'unlimited';
});

const planName = computed(() => {
  const plan = plans.value.find(p => p.id === currentPlanKey.value);
  return plan?.name || t('subscription.freePlan');
});

const planSubtitle = computed(() => {
  const plan = plans.value.find(p => p.id === currentPlanKey.value);
  return plan?.description || t('subscription.perfectForGettingStarted');
});

const formatPeriodEnd = computed(() => {
  if (!subscription.value?.current_period_end) return 'Unknown';
  const date = new Date(subscription.value.current_period_end);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
});

const fetchPlans = async () => {
  try {
    loading.value = true;
    const response = await api.get('/api/plans');
    plans.value = response.data.plans;
  } catch (error) {
    console.error('Failed to fetch plans:', error);
    uiStore.showToast({
      message: 'Failed to load subscription plans',
      type: 'error'
    });
  } finally {
    loading.value = false;
  }
};

const upgradeToPlan = async (planOrId) => {
  // Support both plan object and plan ID string
  const planId = typeof planOrId === 'string' ? planOrId : planOrId.id;
  const plan = typeof planOrId === 'string' ? plans.value.find(p => p.id === planOrId) : planOrId;

  if (planId === 'free' || plan?.is_free) {
    uiStore.showToast({
      message: 'You are already on the free plan',
      type: 'info'
    });
    return;
  }

  try {
    const hasActiveSubscription = subscription.value?.stripe_subscription_id;

    uiStore.showToast({
      message: hasActiveSubscription ? 'Updating your subscription...' : 'Redirecting to Stripe checkout...',
      type: 'info'
    });

    const response = await api.post('/api/subscription/checkout', {
      plan: planId,
      price_id: plan?.price_id
    });

    // If subscription was updated (not redirected to checkout)
    if (response.data.redirect) {
      uiStore.showToast({
        message: 'Subscription updated successfully!',
        type: 'success'
      });

      // Refresh user data and reload page
      await authStore.fetchUser();
      window.location.href = response.data.redirect;
    }
    // Redirect to Stripe Checkout for new subscription
    else if (response.data.url) {
      window.location.href = response.data.url;
    }
  } catch (error) {
    console.error('Failed to update subscription:', error);
    uiStore.showToast({
      message: error.response?.data?.message || 'Failed to process subscription change',
      type: 'error'
    });
  }
};

const getPlanButtonText = (plan) => {
  const planNameShort = plan.name.replace(' Plan', '');
  
  if (!subscription.value?.stripe_subscription_id) {
    return `${t('subscription.upgradePlan')} ${planNameShort}`;
  }

  // Determine if it's an upgrade or downgrade based on price
  const currentPlan = plans.value.find(p => p.id === currentPlanKey.value);
  if (!currentPlan) return `${t('subscription.selectPlan')} ${planNameShort}`;

  if (plan.price > currentPlan.price) {
    return `${t('subscription.upgradePlan')} ${planNameShort}`;
  } else if (plan.price < currentPlan.price) {
    return `${t('subscription.downgradePlan')} ${planNameShort}`;
  } else {
    return `${t('subscription.selectPlan')} ${planNameShort}`;
  }
};

const translateFeature = (feature) => {
  // Try to get translation from subscription.features
  const translationKey = `subscription.features.${feature}`;
  const translated = t(translationKey);
  
  // If translation exists and is different from the key, return it
  if (translated !== translationKey) {
    return translated;
  }
  
  // Otherwise return the original feature
  return feature;
};

const openBillingPortal = async () => {
  try {
    uiStore.showToast({
      message: 'Opening billing portal...',
      type: 'info'
    });

    const response = await api.post('/api/subscription/billing-portal');

    if (response.data.url) {
      window.location.href = response.data.url;
    }
  } catch (error) {
    console.error('Failed to open billing portal:', error);
    uiStore.showToast({
      message: error.response?.data?.message || 'Failed to open billing portal',
      type: 'error'
    });
  }
};

const cancelSubscription = async () => {
  if (!confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing period.')) {
    return;
  }

  try {
    await api.post('/api/subscription/cancel');

    uiStore.showToast({
      message: 'Subscription canceled successfully',
      type: 'success'
    });

    // Refresh user data
    await authStore.fetchUser();
  } catch (error) {
    console.error('Failed to cancel subscription:', error);
    uiStore.showToast({
      message: error.response?.data?.message || 'Failed to cancel subscription',
      type: 'error'
    });
  }
};

const contactSales = () => {
  window.location.href = 'mailto:peter@microweber.com?subject=Unlimited Plan Inquiry';
};

onMounted(async () => {
  await Promise.all([
    authStore.fetchUser(),
    fetchPlans()
  ]);
});
</script>

<style scoped>
/* matches the public Pricing page design */
.subscription-page {
  --violet: #2b82f0; --violet-hover: #1a6fdc; --ink: #1f2933; --muted: #6b7280;
  --line: rgba(31,41,51,0.08); --card: #ffffff; --pill: 999px;
  background: #f4f3f8; min-height: 100vh; padding: 44px 0 80px;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  letter-spacing: -0.01em; color: var(--ink);
}
.subscription-page button, .subscription-page select { font-family: inherit; }
.page-container { max-width: 1120px; margin: 0 auto; padding: 0 24px; }
.page-header { text-align: center; margin-bottom: 40px; }
.page-title { font-size: clamp(30px, 4vw, 48px); font-weight: 700; letter-spacing: -0.035em; color: var(--ink); margin: 0 0 10px; }
.page-subtitle { font-size: clamp(16px, 2vw, 19px); color: var(--muted); margin: 0; }

/* current plan card */
.current-plan-card { position: relative; background: var(--card); border: 1px solid var(--line); border-radius: 24px; padding: 32px 34px; box-shadow: 0 26px 60px -40px rgba(31,41,51,0.3); margin: 0 auto 40px; width: 100%; max-width: 1004px; }
.plan-badge { display: inline-block; font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--violet); background: rgba(43,130,240,0.1); padding: 5px 12px; border-radius: var(--pill); margin-bottom: 14px; }
.current-plan-title { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; color: var(--ink); margin: 0 0 4px; }
.current-plan-subtitle { color: var(--muted); font-size: 15px; margin: 0 0 24px; }
.usage-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.usage-label { font-size: 14px; font-weight: 600; color: var(--ink); }
.usage-count { font-size: 13px; color: var(--muted); font-weight: 600; }
.progress-bar { height: 10px; background: #eef1f6; border-radius: var(--pill); overflow: hidden; }
.progress-fill { height: 100%; background: linear-gradient(90deg, #5aa2ff, #2b82f0); border-radius: var(--pill); transition: width 0.5s ease; }
.progress-fill-premium { background: linear-gradient(90deg, #a78bfa, #7c5cff); }
.usage-note { font-size: 12.5px; color: var(--muted); margin: 14px 0 0; }
.btn-manage-subscription { display: inline-flex; align-items: center; gap: 9px; margin-top: 20px; padding: 11px 22px; border-radius: var(--pill); border: 1px solid var(--line); background: #fff; color: var(--ink); font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; }
.btn-manage-subscription:hover { border-color: var(--violet); color: var(--violet); transform: translateY(-2px); }

/* loading */
.loading-plans { text-align: center; padding: 60px 0; color: var(--muted); }
.spinner { width: 36px; height: 36px; border: 3px solid #e5e9f0; border-top-color: var(--violet); border-radius: 50%; margin: 0 auto 16px; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* plans grid (equal-height, centered like Pricing) */
.plans-grid { display: flex; flex-wrap: wrap; justify-content: center; align-items: stretch; gap: 22px; margin-bottom: 64px; }
.plan-card { position: relative; background: var(--card); border: 1px solid var(--line); border-radius: 24px; padding: 32px 28px; box-shadow: 0 26px 60px -40px rgba(31,41,51,0.3); flex: 1 1 280px; max-width: 320px; display: flex; flex-direction: column; transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease; }
.plan-card:hover { transform: translateY(-6px); box-shadow: 0 36px 80px -36px rgba(31,41,51,0.4); }
.plan-card.featured { border-color: var(--violet); box-shadow: 0 36px 80px -34px rgba(43,130,240,0.45); }
.plan-card.current { border-color: #10b981; }
.featured-badge, .current-plan-badge { position: absolute; top: -13px; left: 50%; transform: translateX(-50%); color: #fff; font-size: 12px; font-weight: 700; padding: 6px 16px; border-radius: var(--pill); white-space: nowrap; }
.featured-badge { background: var(--violet); }
.current-plan-badge { background: #10b981; }
.plan-header { margin-bottom: 20px; }
.plan-name { font-size: 21px; font-weight: 700; color: var(--ink); margin: 0 0 12px; }
.plan-price { display: flex; align-items: baseline; gap: 6px; }
.price-amount { font-size: 38px; font-weight: 800; letter-spacing: -0.03em; color: var(--ink); line-height: 1; }
.price-period { font-size: 14px; color: var(--muted); font-weight: 500; }
.plan-features { list-style: none; margin: 0 0 22px; padding: 22px 0 0; border-top: 1px solid var(--line); display: flex; flex-direction: column; gap: 12px; flex: 1; }
.feature-item { display: flex; align-items: flex-start; gap: 10px; font-size: 14px; color: var(--ink); line-height: 1.45; }
.feature-icon { color: var(--violet); margin-top: 2px; flex-shrink: 0; }
.plan-btn { width: 100%; padding: 13px 22px; border-radius: var(--pill); border: 1px solid var(--line); background: #fff; color: var(--ink); font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; margin-top: auto; }
.plan-btn:hover { border-color: var(--violet); color: var(--violet); transform: translateY(-2px); }
.plan-btn.featured { background: var(--violet); color: #fff; border-color: var(--violet); box-shadow: 0 16px 30px -16px rgba(43,130,240,0.6); }
.plan-btn.featured:hover { background: var(--violet-hover); color: #fff; transform: translateY(-2px); }
.plan-btn.current { background: #f1f3f6; color: var(--muted); cursor: default; border-color: transparent; }
.plan-btn.current:hover { transform: none; color: var(--muted); border-color: transparent; }

/* faq (like Pricing) */
.faq-section { max-width: 880px; margin: 0 auto; }
.faq-title { text-align: center; font-size: clamp(26px, 3.4vw, 40px); font-weight: 700; letter-spacing: -0.03em; color: var(--ink); margin: 0 0 36px; }
.faq-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.faq-item { background: var(--card); border: 1px solid var(--line); border-radius: 18px; padding: 24px 26px; box-shadow: 0 18px 44px -34px rgba(31,41,51,0.3); }
.faq-question { font-size: 16px; font-weight: 700; color: var(--ink); margin: 0 0 8px; letter-spacing: -0.01em; }
.faq-answer { font-size: 14px; color: var(--muted); line-height: 1.6; margin: 0; }

@media (max-width: 768px) {
  .faq-grid { grid-template-columns: 1fr; }
  .plan-card { max-width: 100%; }
}
</style>
